// WebRTC transport (service-ws + OpenAI Realtime), which carries nearly every
// call. Ported from the embed's LandingCallManager: same session, SDP,
// control-socket, finalize and recording behavior, plus it hands both audio
// streams to the conversation for level metering.
import { anonymousIdentity } from './auth.js';
import { mcpTurn } from './mcp-turn.js';
import { toTranscript } from './transcript.js';
import { RECORDING_BITRATE, RECORDING_MIME, uploadTracks, type Tracks } from './recording.js';
import { RuntimeMismatchError, type Transport, type TransportHooks } from './types.js';

// Domain allowlist is enforced upstream (Firebase auth domain + service-ws
// origin policy), not by hiding this URL.
const VOICE_SERVICE_URL = 'https://public.voice.getline.ai';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Json = any;

export const webRtcTransport = (hooks: TransportHooks): Transport => {
  let pc: RTCPeerConnection | null = null;
  let dc: RTCDataChannel | null = null;
  let localStream: MediaStream | null = null;
  let controlWs: WebSocket | null = null;
  let audioEl: HTMLAudioElement | null = null;
  let sessionMeta: { audioStreamPath: string; streamSid: string } | null = null;
  let status: 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error' = 'idle';

  // Recording (uploaded to service-ws after hang-up, as the embed does).
  let recordCtx: AudioContext | null = null;
  let recorders: { mixed: MediaRecorder; inbound: MediaRecorder; outbound: MediaRecorder } | null = null;
  let chunks: Tracks = { mixed: [], inbound: [], outbound: [] };

  // Incremented by hangUp(); call() bails at every await if it no longer
  // matches, so a hang-up while connecting can't come back as connected.
  let gen = 0;
  let t0 = 0;
  let firstResponseLogged = false;

  // End-of-call finalize handshake: after session.end the server sends
  // `finalize`; we forward it to the model and wait for the matching
  // response.done (so saveCallSessionLog is emitted), capped at 10s.
  let finalizeSent = false;
  let finalizeResolver: (() => void) | null = null;
  let finalizeResponseId: string | null = null;

  const ms = () => `${(performance.now() - t0).toFixed(0)}ms`;
  const setStatus = (s: typeof status) => {
    status = s;
    if (s !== 'idle') hooks.status(s);
  };

  const sendControl = (msg: Json) => {
    if (controlWs?.readyState === WebSocket.OPEN) controlWs.send(JSON.stringify(msg));
  };

  const stopAudioPath = () => {
    localStream?.getTracks().forEach((t) => t.stop());
    localStream = null;
    if (audioEl) {
      audioEl.srcObject = null;
      audioEl = null;
    }
  };

  const cleanup = () => {
    if (dc) { try { dc.close(); } catch { /* already closed */ } }
    pc?.close();
    stopAudioPath();
    if (controlWs && controlWs.readyState <= 1) { try { controlWs.close(); } catch { /* already closed */ } }
    pc = null;
    dc = null;
    controlWs = null;
    sessionMeta = null;
    recorders = null;
    chunks = { mixed: [], inbound: [], outbound: [] };
    finalizeSent = false;
    finalizeResolver = null;
    finalizeResponseId = null;
    recordCtx?.close().catch(() => {});
    recordCtx = null;
  };

  const startRecording = async (local: MediaStream, remote: MediaStream) => {
    try {
      const ctx = new AudioContext();
      if (ctx.state === 'suspended') await ctx.resume();
      recordCtx = ctx;
      const localSource = ctx.createMediaStreamSource(local);
      const remoteSource = ctx.createMediaStreamSource(remote);
      const mixedDest = ctx.createMediaStreamDestination();
      localSource.connect(mixedDest);
      remoteSource.connect(mixedDest);
      const inboundDest = ctx.createMediaStreamDestination();
      localSource.connect(inboundDest);
      const outboundDest = ctx.createMediaStreamDestination();
      remoteSource.connect(outboundDest);
      const make = (stream: MediaStream, into: Blob[]) => {
        const r = new MediaRecorder(stream, { mimeType: RECORDING_MIME, audioBitsPerSecond: RECORDING_BITRATE });
        r.ondataavailable = (e) => { if (e.data.size > 0) into.push(e.data); };
        r.start(1000);
        return r;
      };
      recorders = {
        mixed: make(mixedDest.stream, chunks.mixed),
        inbound: make(inboundDest.stream, chunks.inbound),
        outbound: make(outboundDest.stream, chunks.outbound),
      };
    } catch (e) {
      console.warn('[talk:webrtc] recording unavailable:', e);
    }
  };

  const beginFinalize = () =>
    new Promise<void>((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        finalizeResolver = null;
        resolve();
      };
      finalizeResolver = finish;
      setTimeout(finish, 10_000);
    });

  const handleModelEvent = (event: Json) => {
    const transcript = toTranscript(event);
    if (transcript) hooks.transcript(transcript);
    switch (event.type) {
      case 'response.function_call_arguments.done':
        sendControl({ type: 'tool.call', toolCall: event });
        break;
      case 'response.output_audio_transcript.done':
      case 'response.audio_transcript.done':
      case 'conversation.item.input_audio_transcription.completed':
        sendControl({ type: 'transcript', event });
        break;
      case 'response.created':
        hooks.log(`response.created: ${ms()}`);
        // The first response.created after finalize is ours; resolve on its done.
        if (finalizeSent && !finalizeResponseId) finalizeResponseId = event.response?.id || null;
        sendControl({ type: 'event', event });
        break;
      case 'response.output_audio_transcript.delta':
      case 'response.audio_transcript.delta':
        if (!firstResponseLogged) {
          firstResponseLogged = true;
          hooks.log(`first audio token: ${ms()}`);
        }
        break;
      case 'session.created':
        hooks.log(`session.created: ${ms()}`);
        sendControl({ type: 'event', event });
        break;
      case 'response.done':
        sendControl({ type: 'event', event });
        if (finalizeSent && finalizeResponseId && event.response?.id === finalizeResponseId) finalizeResolver?.();
        break;
      case 'session.updated':
      case 'response.audio.done':
      case 'conversation.item.added':
      case 'conversation.item.done':
      case 'output_audio_buffer.started':
      case 'output_audio_buffer.stopped':
      case 'output_audio_buffer.cleared':
      case 'response.output_audio.done':
      case 'input_audio_buffer.speech_started':
      case 'input_audio_buffer.speech_stopped':
        sendControl({ type: 'event', event });
        break;
      case 'error':
        console.error('[talk:webrtc] model error:', JSON.stringify(event.error));
        break;
    }
  };

  const handleControlMessage = (msg: Json) => {
    // Runtime events are for the page, not the model; handle them before the
    // data-channel guard so none are dropped during setup.
    if (msg?.type === 'runtime.event' && msg.event) {
      hooks.event(msg.event);
      return;
    }
    if (!dc || dc.readyState !== 'open') return;
    switch (msg.type) {
      case 'tool.result': {
        dc.send(JSON.stringify({
          type: 'conversation.item.create',
          previous_item_id: msg.itemId,
          item: {
            type: 'function_call_output',
            call_id: msg.callId,
            output: typeof msg.output === 'string' ? msg.output : JSON.stringify(msg.output),
          },
        }));
        const payload: Json = { type: 'response.create' };
        if (msg.instructions?.text) payload.response = { instructions: msg.instructions.text };
        dc.send(JSON.stringify(payload));
        if (msg.sessionTools?.length) {
          dc.send(JSON.stringify({ type: 'session.update', session: { type: 'realtime', tools: msg.sessionTools } }));
        }
        break;
      }
      case 'session.update':
        dc.send(JSON.stringify({ type: 'session.update', session: msg.session }));
        break;
      case 'finalize':
        // Cancel any in-flight response first, or the model rejects the
        // finalize response.create and saveCallSessionLog never arrives.
        finalizeSent = true;
        finalizeResponseId = null;
        dc.send(JSON.stringify({ type: 'response.cancel' }));
        if (Array.isArray(msg.payload)) for (const m of msg.payload) dc.send(JSON.stringify(m));
        break;
    }
  };

  const openControlSocket = (token: string) => {
    const meta = sessionMeta;
    if (!meta) return;
    const base = VOICE_SERVICE_URL.replace(/^http/, 'ws');
    const ws = new WebSocket(`${base}/webrtc/control?audioStreamPath=${encodeURIComponent(meta.audioStreamPath)}&streamSid=${encodeURIComponent(meta.streamSid)}&token=${encodeURIComponent(token)}`);
    controlWs = ws;
    ws.onmessage = (e) => {
      try { handleControlMessage(JSON.parse(e.data)); } catch { /* non-JSON frame */ }
    };
  };

  const initializeSession = (callerContext: unknown, openingResponse: unknown) => {
    const channel = dc;
    if (!channel) return;
    const init = () => {
      hooks.log(`data channel open: ${ms()}`);
      if (callerContext) {
        const text = typeof callerContext === 'string' ? callerContext : JSON.stringify(callerContext, null, 2);
        channel.send(JSON.stringify({
          type: 'conversation.item.create',
          previous_item_id: null,
          item: { type: 'message', role: 'user', content: [{ type: 'input_text', text: `<CallerContext>\n${text}\n</CallerContext>` }] },
        }));
      }
      // The greeting. service-ws sends the opening turn it wants (the agent's
      // greeting, verbatim when greetingLiteral), as a phone call opens;
      // without one the model opens on its own.
      channel.send(JSON.stringify(openingResponse || { type: 'response.create' }));
    };
    if (channel.readyState === 'open') init();
    else channel.addEventListener('open', init, { once: true });
  };

  const call = async (agent: string, token: string | null) => {
    const my = ++gen;
    const aborted = () => my !== gen;
    try {
      firstResponseLogged = false;
      setStatus('connecting');
      t0 = performance.now();

      // Auth: a server-minted token, or anonymous Firebase.
      let bearer: string;
      let otherLine: string | undefined;
      if (token) {
        bearer = token;
      } else {
        const id = await anonymousIdentity();
        bearer = id.idToken;
        otherLine = `client:${id.uid}`;
      }
      hooks.log(`auth (${token ? 'token' : 'anonymous'}): ${ms()}`);
      if (aborted()) return;

      const assistantLine = agent.startsWith('+') ? agent : `+1${agent}`;

      // Session and microphone in parallel.
      const [session, stream] = await Promise.all([
        fetch(`${VOICE_SERVICE_URL}/webrtc/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${bearer}` },
          body: JSON.stringify({ assistantLine, ...(otherLine ? { otherLine } : {}), role: 'receiver' }),
        }).then(async (r) => {
          if (!r.ok) {
            const err = await r.json().catch(() => ({}));
            if (r.status === 409) throw new RuntimeMismatchError(err.runtime || 'grok');
            throw new Error(err.error || `Session creation failed: ${r.status}`);
          }
          return r.json();
        }),
        navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 24000 } }),
      ]);
      hooks.log(`session + microphone: ${ms()}`);

      if (aborted()) {
        // Mic granted after the hang-up; release it so the indicator goes off.
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      sessionMeta = { audioStreamPath: session.audioStreamPath, streamSid: session.streamSid };
      localStream = stream;
      hooks.callerStream(stream);

      const peer = new RTCPeerConnection();
      pc = peer;
      stream.getAudioTracks().forEach((t) => peer.addTrack(t, stream));
      dc = peer.createDataChannel('oai-events');

      peer.ontrack = (event) => {
        const remote = event.streams?.[0];
        if (!remote) return;
        hooks.log(`remote audio: ${ms()}`);
        // An <audio> element plays it; Chrome only feeds remote WebRTC audio
        // into Web Audio while an element is attached, so this also keeps the meter alive.
        const audio = new Audio();
        audio.srcObject = remote;
        audio.autoplay = true;
        audioEl = audio;
        hooks.agentStream(remote);
        startRecording(stream, remote).catch((e) => console.error('[talk:webrtc] recording error:', e));
      };

      const offer = await peer.createOffer();
      await peer.setLocalDescription(offer);
      if (aborted()) { cleanup(); return; }

      const sdp = await fetch('https://api.openai.com/v1/realtime/calls', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.ephemeralKey}`, 'Content-Type': 'application/sdp' },
        body: offer.sdp,
      });
      if (!sdp.ok) throw new Error(`WebRTC negotiation failed: ${sdp.status}`);
      await peer.setRemoteDescription({ type: 'answer', sdp: await sdp.text() });
      if (aborted()) { cleanup(); return; }

      openControlSocket(token ?? bearer);
      if (dc) {
        const channel = dc;
        const turn = mcpTurn((m) => { if (channel.readyState === 'open') channel.send(JSON.stringify(m)); }, () => finalizeSent);
        dc.onmessage = (e) => {
          try {
            const event = JSON.parse(e.data);
            turn(event);
            handleModelEvent(event);
          } catch { /* non-JSON */ }
        };
        dc.onclose = () => { if (status === 'connected') hangUp(); };
      }
      initializeSession(session.callerContext, session.openingResponse);

      setStatus('connected');
      hooks.log(`connected: ${ms()}`);
    } catch (err) {
      if (err instanceof RuntimeMismatchError) { cleanup(); throw err; }
      if (aborted()) { cleanup(); return; }
      hooks.error(err instanceof Error ? err.message : 'Failed to connect');
      setStatus('error');
      cleanup();
    }
  };

  const hangUp = () => {
    if (status === 'idle' || status === 'disconnected') return;
    gen++;
    setStatus('disconnected');

    // Phase 1: stop what the caller can perceive (mic, playback) right away.
    // The data channel stays up for the finalize round-trip.
    stopAudioPath();

    const flushed = Promise.all(
      (recorders ? [recorders.mixed, recorders.inbound, recorders.outbound] : []).map((rec) => {
        if (rec.state === 'inactive') return Promise.resolve();
        return new Promise<void>((resolve) => {
          rec.onstop = () => resolve();
          try { rec.stop(); } catch { resolve(); }
        });
      }),
    );

    // Phase 2: finalize (capped at 10s), then tear down, which ends the call
    // and its billing. The recording uploads on its own afterwards: a long
    // call's upload must never hold the call open.
    const finalized = controlWs?.readyState === WebSocket.OPEN ? beginFinalize() : Promise.resolve();
    if (controlWs?.readyState === WebSocket.OPEN) controlWs.send(JSON.stringify({ type: 'session.end' }));
    const meta = sessionMeta;
    const tracks = chunks;
    if (meta) flushed.then(() => uploadTracks(`${VOICE_SERVICE_URL}/webrtc/recording`, meta, tracks));
    Promise.allSettled([finalized, flushed]).then(cleanup);
  };

  const setMuted = (muted: boolean) => {
    localStream?.getAudioTracks().forEach((t) => { t.enabled = !muted; });
  };

  return { call, hangUp, setMuted };
};
